import { Loader2, Pause, Play } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Carousel } from '@/components/ui/Carousel'
import { apiUrl } from '@/lib/apiUrl'
import { cn } from '@/lib/cn'
import styles from './VoiceCarousel.module.scss'

export interface VoiceOption {
  value: string
  name: string
  gender: 'masculina' | 'feminina' | 'male' | 'female'
  /** CSS gradient string, e.g. `"linear-gradient(135deg, #FF7A3D, #E8431A)"`. */
  gradient: string
}

export interface VoiceCarouselProps {
  options: ReadonlyArray<VoiceOption>
  value: string
  onValueChange: (value: string) => void
  /** TTS provider (edge|openai). Forwarded to the preview endpoint. */
  provider?: string
}

/**
 * Horizontal carousel of voice cards. Clicking a card selects the voice;
 * clicking the inner "Preview" button plays a short sample from
 * `/api/v1/media/tts-preview` while showing a pulsing waveform. Only one
 * audio stream plays at a time — triggering a new preview stops the
 * previous one.
 */
export function VoiceCarousel({ options, value, onValueChange, provider }: VoiceCarouselProps) {
  const [playing, setPlaying] = useState<string | null>(null)
  const [loading, setLoading] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // Track the cleanup handler for the current Audio so a fast voice swap
  // (A → B before A starts) can cancel A's listeners. Without this, A's
  // ``playing`` event would fire late and overwrite B's state.
  const cleanupRef = useRef<(() => void) | null>(null)

  const stop = useCallback(() => {
    const a = audioRef.current
    if (a) {
      a.pause()
      a.currentTime = 0
    }
    cleanupRef.current?.()
    cleanupRef.current = null
    audioRef.current = null
    setPlaying(null)
    setLoading(null)
  }, [])

  const togglePreview = useCallback(
    (voice: string) => {
      if (playing === voice || loading === voice) {
        stop()
        return
      }
      stop()
      // Provider param is intentionally omitted when the caller didn't
      // supply one — the backend's `get_provider("")` returns the default
      // (Edge TTS) and accepts the implicit choice. Sending an explicit
      // "edge" string can mismatch provider keys on some installs.
      const params = new URLSearchParams({ voice })
      if (provider) params.set('provider', provider)
      const url = apiUrl(`/api/v1/media/tts-preview?${params.toString()}`)
      const a = new Audio(url)
      audioRef.current = a
      setLoading(voice)
      // ``playing`` fires when audio actually starts producing sound — that's
      // when we flip the button from spinner to waveform. ``play()`` resolves
      // earlier (only "playback was permitted"), which would show a waveform
      // over silence on slow Edge cold starts.
      const onPlaying = () => {
        setLoading(null)
        setPlaying(voice)
      }
      const onEnded = () => {
        setPlaying(null)
      }
      const onError = () => {
        console.error('[VoiceCarousel] preview failed', voice, a.error)
        setLoading(null)
        setPlaying(null)
      }
      a.addEventListener('playing', onPlaying)
      a.addEventListener('ended', onEnded)
      a.addEventListener('error', onError)
      cleanupRef.current = () => {
        a.removeEventListener('playing', onPlaying)
        a.removeEventListener('ended', onEnded)
        a.removeEventListener('error', onError)
      }
      a.play().catch((err: unknown) => {
        // ``AbortError`` is the documented rejection when ``pause()`` is
        // called before the in-flight ``play()`` resolves — exactly what
        // happens when the user clicks another preview while this one
        // is still loading. ``stop()`` already cleared loading/playing,
        // so this is benign; logging it would only confuse.
        if (err instanceof DOMException && err.name === 'AbortError') return
        console.error('[VoiceCarousel] play() rejected', voice, err)
        setLoading(null)
        setPlaying(null)
      })
    },
    [playing, loading, provider, stop],
  )

  useEffect(() => {
    return () => {
      // Stop any in-flight preview when the component unmounts (e.g., the
      // user submits the form and moves to the JobProgressPanel).
      const a = audioRef.current
      if (a) {
        a.pause()
      }
      cleanupRef.current?.()
    }
  }, [])

  return (
    <Carousel itemWidth={190} aria-label="Vozes">
      {options.map((opt) => {
        const isActive = opt.value === value
        const isPlaying = playing === opt.value
        const isLoading = loading === opt.value
        const isBusy = isPlaying || isLoading
        return (
          <label
            key={opt.value}
            className={cn(styles.card, isActive && styles.active)}
          >
            <input
              type="radio"
              name="voice-preview"
              className={styles.srInput}
              checked={isActive}
              onChange={() => {
                onValueChange(opt.value)
              }}
            />
            <div className={styles.head}>
              <div className={styles.avatar} style={{ background: opt.gradient }}>
                {opt.name.charAt(0).toUpperCase()}
              </div>
              <div className={styles.meta}>
                <span className={styles.name}>{opt.name}</span>
                <span className={styles.gender}>{opt.gender}</span>
              </div>
            </div>
            <button
              type="button"
              className={cn(styles.previewBtn, isBusy && styles.previewBtnPlaying)}
              onClick={(e) => {
                e.preventDefault()
                togglePreview(opt.value)
              }}
              aria-label={
                isPlaying
                  ? `Parar preview de ${opt.name}`
                  : isLoading
                    ? `Carregando preview de ${opt.name}`
                    : `Ouvir preview de ${opt.name}`
              }
            >
              {isLoading ? (
                <>
                  <Loader2 className={styles.spinner} aria-hidden />
                  <span>Carregando…</span>
                </>
              ) : isPlaying ? (
                <>
                  <Pause aria-hidden />
                  <span className={styles.waveform} aria-hidden>
                    <i />
                    <i />
                    <i />
                    <i />
                  </span>
                </>
              ) : (
                <>
                  <Play aria-hidden />
                  <span>Preview</span>
                </>
              )}
            </button>
          </label>
        )
      })}
    </Carousel>
  )
}
